import React, { useState } from "react";
import {
    Box,
    Grid,
    TextField,
    InputAdornment,
    Typography,
    Button,
} from "@mui/material";
import InfoButton from "../../Tooltip/InfoTextField";
import { PaymentProviderConfigViewModel } from "../../../Types/ExportTypes";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "../../../Context/Contexts";

type Props = {
    viewModel: PaymentProviderConfigViewModel | null;
    onSave: (vm: PaymentProviderConfigViewModel) => void;
};

const DefaultPaymentProviderWizard: React.FC<Props> = ({ viewModel, onSave }) => {
    const { t } = useTranslation();
    const { showSnackbar } = useSnackbar();
    const [localViewModel, setLocalViewModel] = useState<PaymentProviderConfigViewModel>(viewModel ?? {});

    const handleChange = (field: keyof PaymentProviderConfigViewModel, value: string | null) => {
        setLocalViewModel((prev) => ({ ...prev, [field]: value }));
    };

    const validateViewModel = () => {
        if (!localViewModel.description?.trim()) {
            showSnackbar(t("help.descriptionRequired"), "warning");
            return false;
        }


        return true;
    };

    const handleFinish = () => {
        if (!validateViewModel()) return;
        onSave(localViewModel);
    };

    const cancelWizard = () => {
        window.history.back();
    };

    return (
        <Box sx={{ width: "100%", maxWidth: 600, mx: "auto", px: 2 }}>
            <Typography variant="h6" gutterBottom>
                {t("common.default")}
            </Typography>

            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TextField
                        label={t("common.description")}
                        value={localViewModel.description || ""}
                        onChange={(e) => handleChange("description", e.target.value)}
                        fullWidth
                        margin="normal"
                        error={!localViewModel.description?.trim()}
                        helperText={
                            !localViewModel.description?.trim()
                                ? t("help.descriptionRequired")
                                : ""
                        }
                    />
                </Grid>

                {Array.from({ length: 20 }, (_, i) => {
                    const n = i + 1;
                    const key = `paymentProviderInfo${n}` as keyof PaymentProviderConfigViewModel;
                    const rawValue = localViewModel[key] as string | null;

                    return (
                        <Grid item xs={12} key={n}>
                            <TextField
                                label={t("common.paymentProviderKey", { index: n })}
                                value={rawValue ?? ""}
                                onChange={(e) => handleChange(key, e.target.value)}
                                fullWidth
                                margin="normal"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <InfoButton infoText={t("help.fallbackKey", { index: n })} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                    );
                })}
            </Grid>

            <Box sx={{ marginTop: 2, textAlign: "center" }}>
                <Button variant="contained" color="primary" onClick={handleFinish} sx={{ mr: 2 }}>
                    {t("common.finish")}
                </Button>
                <Button variant="contained" color="warning" onClick={cancelWizard}>
                    {t("common.cancelWizard")}
                </Button>
            </Box>
        </Box>
    );
};

export default DefaultPaymentProviderWizard;
